/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEqual, isEmpty } from 'lodash';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { withRouter } from 'react-router-dom';
import { ShepherdTour } from 'react-shepherd';
import { offset } from '@floating-ui/dom';
import subscribe from '../utils/subscribe';
import Notification from './Common/notification';
import ModalWrapper from './Common/modalWrapper';
import Default from '../constants/defaults';
import { openModal, closeModal } from '../actions/modalActions';
import { removeAllMenus } from '../actions/dropDownActions';
import { getOrganizations } from '../actions/organizationActions';
import { fetchTeamDefaultOptions } from '../actions/defaultOptionsActions';
import {
  fetchAllWorkPackages,
  setSelectedSpace
} from '../actions/workPackageActions';
import { getAllDeployments } from '../actions/deploymentActions';
import { fetchAllTasks } from '../actions/taskActions';
import { getAllCadences, getCadence } from '../actions/cadenceActions';
import { fetchResultsCriteria } from '../actions/criteriaActions';
import { getAllTargets } from '../actions/targetActions';
import { getAllProducts } from '../actions/productActions';
import {
  fetchUsers,
  logout,
  activateSpacesSpinner,
  updateUserSpaceView
} from '../actions/userActions';
import { fetchTeams } from '../actions/teamActions';
import { fetchAllTaskTypes } from '../actions/taskTypeAction';
import { hideNotification } from '../actions/notificationActions';
import openModalFromParams from '../utils/deepLink';
import Types from '../constants/actionTypes';
import ModalErrorBoundary from './ErrorBoundaries/ModalErrorBoundary';

class App extends React.Component {
  state = {
    subscribedToActionCable: false,
    largeSpinning: false,
    smallSpinning: false,
    closeFunction: false
  };

  componentWillMount() {
    const {
      userLoggedIn,
      isFetching,
      dataLoading,
      userGot401,
      location,
      history
    } = this.props;
    if (
      location.search.includes('itemId=undefined') ||
      location.search.includes('itemId=NaN') ||
      location.search.includes('itemId=message')
    ) {
      history.push({
        search: ''
      });
    }

    window.addEventListener('message', this.fetchCriteriaOnTeamCriteriaChange);

    // TODO: we really could/should put this stuff in its own file and import it
    // Heartbeat control structure and definitions
    window.App.heartbeat = {
      logging: false,

      // Main function that verifies if we got a heartbeat every X seconds and acts accordingly
      checkForHeartbeat() {
        if (this.appIsLoading) return true;

        // this.subscriptions is an ActionCable subscription object
        const conn = this.subscription.consumer.connection;

        this.log(
          'Checking we got a heartbeat. Last one was at',
          this.lastTick,
          ", it's now",
          Date.now()
        );

        // Check that we have a heartbeat from in time
        if (this.shouldReloadAppData()) {
          this.log(
            'Last heartbeat was over',
            Default.HEARTBEAT_TIMEOUT,
            'seconds ago.'
          );
          this.log(
            'Closing/opening ActionCable connection and reloading the space'
          );

          console.log(
            'Last heartbeat was over',
            Default.HEARTBEAT_TIMEOUT,
            'seconds ago.'
          );
          console.log(
            'Closing/opening ActionCable connection and reloading the space'
          );

          this.reloadAppData();
        } else {
          // We check isActive() and not isOpen() because the former checks for both Open and Connecting statuses
          if (!conn.isActive()) {
            console.log(
              "It's been at least 5 seconds since the last heartbeat and ActionCable connection isn't open, reopening"
            );

            conn.reopen();
          }
        }
      },

      // The condition in which we trigger a space reload, in this case, 30 seconds have passed since the last tick
      shouldReloadAppData() {
        return Date.now() > this.lastTick + Default.HEARTBEAT_TIMEOUT * 1000;
      },

      // Called by other functions to reset the heartbeat timer
      resetHeartbeatTimer() {
        this.lastTick = Date.now();
      },

      // Get the heartbeat check rolling, called when the app is done loading the space
      startHeartbeatMonitor() {
        this.checkTimerId = setInterval(() => {
          this.checkForHeartbeat();
        }, Default.HEARTBEAT_INTERVAL * 1000);
      },

      // Stop the heartbeat checks, called when the app starts loading the space
      stopHeartbeatMonitor() {
        clearInterval(this.checkTimerId);

        this.checkTimerId = null;
      },

      // This is triggered by the ActionCable subscription every time a heartbeat is received
      receive() {
        // If the App is currently loading something, don't process any heartbeats
        if (this.appIsLoading) return;

        // Why? Because we might lose network for a couple minutes, re-connect, and the first thing arriving is a heartbeat.
        // We'd think all was fine but we actually needed to reload the space
        if (this.shouldReloadAppData()) this.checkForHeartbeat();

        // Reset the heartbeat timer
        this.resetHeartbeatTimer();

        this.log('Got heartbeat at ', this.lastTick);
      },

      // This is called by the application when its loading status changes. This
      // a situation when we'd reset the timer, reload the space, the reload takes >30 seconds, then we'd trigger a reload again
      appLoadingStatusChanged(
        appStartedLoading,
        appDoneLoading,
        userIsLoggedIn
      ) {
        if (appStartedLoading) {
          this.appIsLoading = true;

          this.log(
            'App started loading data, stopping the timed heartbeat checks'
          );
          this.stopHeartbeatMonitor();
        }

        if (appDoneLoading) {
          this.appIsLoading = false;

          this.log(
            'App is done loading data, resetting heartbeat timer and starting timed checks'
          );

          this.resetHeartbeatTimer();

          if (userIsLoggedIn) this.startHeartbeatMonitor();
        }
      },

      log(...msg) {
        if (this.logging) console.log(...msg);
      },

      // This is initialized below in componentDidUpdate, reason is don't want to interfere with app state ourselves
      reloadAppData: () => {},

      // Used internally
      lastTick: Date.now(), // Holds the last heartbeat time
      appIsLoading: false, // Whether the app is loading the space
      checkTimerId: null, // Holds the Javascript interval/timout ID
      subscription: null // Holds the ActionCable subscription object for the heartbeat, if we ever need it
    };

    this.fetchAppInfo(userLoggedIn, isFetching, dataLoading, userGot401);
  }

  componentWillReceiveProps(nextProps) {
    const {
      userLoggedIn,
      dataLoading,
      isFetching,
      userGot401,
      activeSpacesSpinner
    } = nextProps;
    const { pathname } = this.props.location;
    if (!this.state.largeSpinning && !activeSpacesSpinner) {
      this.fetchAppInfo(
        userLoggedIn,
        isFetching,
        dataLoading,
        userGot401,
        activeSpacesSpinner
      );
    }

    if (
      !this.state.smallSpinning &&
      activeSpacesSpinner &&
      !this.state.closeFunction
    ) {
      this.fetchAppInfo(
        userLoggedIn,
        isFetching,
        dataLoading,
        userGot401,
        activeSpacesSpinner
      );
    }

    if (
      userLoggedIn &&
      nextProps.location.search &&
      (!isEqual(this.props.location, nextProps.location) || dataLoading) &&
      isEmpty(this.props.modal.modalInfo)
    )
      openModalFromParams(nextProps.location.search);

    // if (
    //   !this.state.largeSpinning &&
    //   products.length === 0 &&
    //   !pathname.includes('/planwall/r/5')
    // )
    //   this.props.history.push('/planwall/r/5');

    // fetch tasks if we changed to cardwall
    if (
      !pathname.includes('cardwall') &&
      nextProps.location.pathname.includes('cardwall')
    )
      this.fetchAllTasks();
  }

  componentDidUpdate(prevProps, prevState) {
    const appIsLoading = this.state.largeSpinning || this.state.smallSpinning;
    const appWasLoading = prevState.largeSpinning || prevState.smallSpinning;

    const appDoneLoading = appWasLoading && !appIsLoading;
    const appStartedLoading = appIsLoading && !appWasLoading;

    // These functions are called in every main app status change, the heartbeat object will decide if it needs to do something
    window.App.heartbeat.appLoadingStatusChanged(
      appStartedLoading,
      appDoneLoading,
      this.props.userLoggedIn
    );

    // We define this here because it's not up to the heartbeat object to know how to reload the space
    window.App.heartbeat.reloadAppData = function () {
      this.props.activateSpacesSpinner(true);
    }.bind(this);

    if (
      this.props.userGot401 &&
      prevProps.isFetching &&
      !this.props.isFetching &&
      this.props.userLoggedIn
    ) {
      this.props.logout();
      this.props.history.push('/login');
    }
  }

  fetchAppInfo = async (
    userLoggedIn,
    isFetching,
    dataLoading,
    userGot401,
    activeSpacesSpinner
  ) => {
    // if you are to ever have the misfortune of refactoring this code, call me first
    // (347)-572-9598 ~ Jonathan

    if (
      ((!isFetching && dataLoading) || activeSpacesSpinner) &&
      !userGot401 &&
      userLoggedIn
    ) {
      if (activeSpacesSpinner && !this.state.smallSpinning) {
        // this only happens when we switch spaces
        this.setState({ smallSpinning: true }, async () => {
          await this.props.fetchUsers(activeSpacesSpinner);
          await this.props.getAllCadences();
          // await this.props.getAllProducts();
          // await this.props.fetchTeams();
          // await this.props.getOrganizations();

          await this.props.fetchTeamDefaultOptions();
          await this.props.getAllTargets();
          await this.fetchAllTasks();
          await this.fetchWorkPackages();
          // await this.props.getAllDeployments();
          this.props.activateSpacesSpinner(false);
          this.setState({ smallSpinning: false });
        });
        return;
      }

      this.setState({ largeSpinning: true }, async () => {
        // happens on authentication and refresh
        // please maintain this order of api calls
        await this.props.getAllProducts();
        await this.props.fetchTeams();
        await this.props.getOrganizations();
        await this.props.fetchUsers(activeSpacesSpinner);
        await this.props.getAllCadences();
        await this.props.fetchTeamDefaultOptions();
        await this.props.getAllTargets();
        await this.fetchAllTasks();

        await this.fetchWorkPackages();

        // await this.props.getAllDeployments();
        this.setState({ largeSpinning: false });
      });
    }

    if (!userLoggedIn && this.state.subscribedToActionCable) {
      this.setState({ subscribedToActionCable: false });
    } else if (!this.state.subscribedToActionCable && userLoggedIn) {
      subscribe(() => this.setState({ subscribedToActionCable: true }));
    }
  };

  fetchCadenceData = async () => {
    await this.fetchAllTasks();
    await this.fetchWorkPackages();
    this.props.getAllCadences();
    this.props.getAllTargets();
  };

  fetchWorkPackages = async () => {
    await this.props.fetchAllWorkPackages();
  };

  fetchAllTasks = () => {
    if (this.props.initialTaskLoad || this.props.activeSpacesSpinner) {
      this.props.fetchAllTasks();
      this.props.fetchAllTaskTypes();
    }
  };

  fetchCriteriaOnTeamCriteriaChange = (e) => {
    // this event listener is specifically defined here due to limitations of the current
    // criteriaReducer architecture. There is a second event listener present in ./CardWall/criteriaView
    // those event listeners are present there such that when there is an update and a user is on that view,
    // then that view will update automatically. If they are not on the view, the eventListner would be removed
    // because the simple act of opening the criteriaView refreshes the criteria on the wall.

    // However, when we delete a criterion, those updates are not handled as nicely as the create/update actions.
    // if a criterion is deleted and a user is not on the criteriaView, then opening up the view will not
    // automatically reflect the changes made. As a result, this global event listener is created to detect
    // a criterion deletion from wherever the user is while they are using the app.

    // for further details on the nuances of this functionality, see /reducers/criteriaReducer

    // The message that triggers this event is posted from either ./Criteria/manageCriteria handleDelete()
    // or from a broadcast event sent from a redux action in criteriaActions

    if (e.data.type === 'criteria') {
      if (e.data.action === 'delete') {
        const adjustedCriteria = this.props.criteria[e.data.filter];

        const resultIds = Object.keys(adjustedCriteria).reduce((acc, id) => {
          if (id === 'team') return acc;
          acc.push(parseInt(id, 10));
          return acc;
        }, []);

        this.props.fetchResultsCriteria(resultIds, e.data.filter, 'delete');
      }
    }
  };

  render() {
    const { notice, modal, children, activeSpacesSpinner } = this.props;
    if (this.state.largeSpinning && !activeSpacesSpinner) {
      return <div id="loader" />;
    }

    const tourOptions = {
      defaultStepOptions: {
        cancelIcon: {
          enabled: true
        },
        floatingUIOptions: {
          middleware: [offset({ mainAxis: 10, crossAxis: 12 })]
        },
      },
      useModalOverlay: false
    };

    return (
      <>
        <Notification
          showNotification={notice.showNotification}
          hideNotification={this.props.hideNotification}
          notificationType={notice.notificationType}
          message={notice.message || 'Something here'}
        />
{/*        <ShepherdTour steps={[]} tourOptions={tourOptions}>
*/}          {children}
        {modal.openModals.map((m) => {
          const modalId = m;
          const modalProps = modal.modalInfo[m];
          return (
            <ModalErrorBoundary key={`error-boundary-${modalId}}`}>
                <ModalWrapper
                  key={modalId}
                  id={modalId || ''}
                  type={modalProps.type}
                  modal={modalProps}
                  close={
                    modalProps.props && modalProps.props.onClose
                      ? modalProps.props.onClose
                      : this.props.closeModal
                  }
                />
            </ModalErrorBoundary>
          );
        })}
        {/*</ShepherdTour>*/}
      </>
    );
  }
}

App.propTypes = {
  children: PropTypes.object,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  modal: PropTypes.object,
  notice: PropTypes.object,
  isFetching: PropTypes.bool,
  dataLoading: PropTypes.bool,
  userLoggedIn: PropTypes.bool,
  defaultOptionsFetched: PropTypes.number,
  fetchTeamDefaultOptions: PropTypes.func,
  fetchAllWorkPackages: PropTypes.func,
  getAllDeployments: PropTypes.func,
  getOrganizations: PropTypes.func,
  getAllCadences: PropTypes.func,
  getAllTargets: PropTypes.func,
  fetchAllTasks: PropTypes.func,
  fetchUsers: PropTypes.func
};

App.defaultProps = {
  currentTeams: [],
  currentProducts: []
};

const mapStateToProps = (state, ownProps) => {
  const { userGot401 } = state.userReducer;
  const isFetching =
    state.workPackageReducer.isFetching ||
    state.taskReducer.isFetching ||
    state.cadenceReducer.isFetching ||
    state.targetReducer.isFetching ||
    state.organizationReducer.isFetching ||
    state.defaultOptionsReducer.isFetching ||
    state.productReducer.isFetching ||
    state.teamReducer.isFetching ||
    state.userReducer.isFetching;

  let initialDataLoading =
    state.organizationReducer.initialDataLoading ||
    state.workPackageReducer.initialDataLoading ||
    state.cadenceReducer.initialDataLoading ||
    state.userReducer.initialDataLoading;
  if (ownProps.location.pathname.includes('cardwall'))
    initialDataLoading =
      initialDataLoading || state.taskReducer.initialDataLoading;
  if (ownProps.location.pathname.includes('/planwall/r/5'))
    initialDataLoading =
      initialDataLoading ||
      (state.organizationReducer.initialDataLoading &&
        !state.organizationReducer.isFetching) ||
      (state.userReducer.initialDataLoading && !state.userReducer.isFetching);
  if (ownProps.location.pathname.includes('/targets'))
    initialDataLoading =
      initialDataLoading || state.targetReducer.initialDataLoading;

  return {
    userGot401,
    isFetching,
    modal: state.modalReducer,
    userLoggedIn: state.userReducer.isLoggedIn,
    dataLoading: initialDataLoading,
    initialTaskLoad: state.taskReducer.initialDataLoading,
    initialWorkPackageLoad: state.workPackageReducer.initialDataLoading,
    initialProductLoad: state.productReducer.initialDataLoading,
    notice: state.notificationReducer,
    products: state.productReducer.products,
    activeSpacesSpinner: state.userReducer.activeSpacesSpinner,
    currentUser: state.userReducer.user,
    criteria: state.criteriaReducer.criteria
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      openModal,
      closeModal,
      fetchUsers,
      getAllTargets,
      fetchAllTasks,
      getAllCadences,
      getOrganizations,
      getAllDeployments,
      fetchAllWorkPackages,
      fetchTeamDefaultOptions,
      getAllProducts,
      fetchTeams,
      removeAllMenus,
      fetchAllTaskTypes,
      logout,
      hideNotification,
      activateSpacesSpinner,
      updateUserSpaceView,
      getCadence,
      setSelectedSpace,
      fetchResultsCriteria
    },
    dispatch
  );

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(DragDropContext(HTML5Backend)(App))
);
